import { apiClient } from '@kss/api';
import { getTenantHeaderInlined, getPrintBrandingInlined, printQrUrl } from './branding';
import {
  printSalesOrder, printPaymentReceipt,
  type SalesOrderPrintInput, type SalesOrderPrintLine, type PartyBlock,
} from './templates';

/**
 * Print a registry-grid document (SalesOrder, SalesReturn, PurchaseOrder,
 * SalesPayment, …) through the PROPER branded template — fetching the REAL
 * line-item table + resolving the party — instead of the generic key-value
 * `buildDocHtml` that only read the truncated header `description` envelope
 * (which is why prints showed "only 2 products" with no company/customer).
 *
 * Returns true if it handled the resource; false → caller should fall back to
 * its generic print. Read-only: GETs only, never writes.
 */

const r2 = (n: number) => Math.round(n * 100) / 100;

function listOf(d: unknown): Array<Record<string, unknown>> {
  const b = (d ?? {}) as Record<string, unknown>;
  return (Array.isArray(b) ? b
    : Array.isArray(b.items) ? b.items
    : Array.isArray(b.data) ? b.data
    : Array.isArray((b.data as Record<string, unknown>)?.items) ? (b.data as { items: unknown[] }).items
    : []) as Array<Record<string, unknown>>;
}
async function getList(path: string): Promise<Array<Record<string, unknown>>> {
  try { return listOf((await apiClient.get(path)).data); } catch { return []; }
}
async function resolveParty(basePath: string, id: unknown): Promise<Record<string, unknown>> {
  if (id == null || String(id) === '0') return {};
  const rows = await getList(`${basePath}?id=${encodeURIComponent(String(id))}`);
  const hit = rows.find((r) => String(r.id ?? '') === String(id));
  if (hit) return hit;
  const all = await getList(basePath);
  return all.find((r) => String(r.id ?? '') === String(id)) ?? {};
}

interface DocCfg {
  docTitle: string; lineBasePath: string; lineFk: string;
  partyBasePath: string; partyIdField: string; partyLabel: string;
  numberField: string; dateField: string; totalField: string;
  /** When the line table is empty, recover line items from the header
   *  `description` JSON envelope ({ lines: [{ name, quantity, unit }] }).
   *  DeliveryChallan keeps its items there (no dedicated line rows on save). */
  descFallback?: boolean;
}
const DOC: Record<string, DocCfg> = {
  SalesOrder: { docTitle: 'SALES ORDER', lineBasePath: '/sales/SalesOrderLine', lineFk: 'salesOrderId', partyBasePath: '/crm/Customer', partyIdField: 'customerId', partyLabel: 'Bill To (Receiver)', numberField: 'orderNumber', dateField: 'orderDate', totalField: 'grandTotal' },
  SalesReturn: { docTitle: 'SALES RETURN', lineBasePath: '/sales/SalesReturnLine', lineFk: 'salesReturnId', partyBasePath: '/crm/Customer', partyIdField: 'customerId', partyLabel: 'Customer', numberField: 'returnNumber', dateField: 'returnDate', totalField: 'refundAmount' },
  PurchaseOrder: { docTitle: 'PURCHASE ORDER', lineBasePath: '/purchase/PurchaseOrderLine', lineFk: 'purchaseOrderId', partyBasePath: '/purchase/Supplier', partyIdField: 'supplierId', partyLabel: 'Vendor', numberField: 'orderNumber', dateField: 'orderDate', totalField: 'grandTotal' },
  // Credit/Debit notes were falling through to the raw key-value dump (no
  // branding, "not full format"). Route them through the full branded layout.
  CreditNote: { docTitle: 'CREDIT NOTE', lineBasePath: '/sales/CreditNoteLine', lineFk: 'creditNoteId', partyBasePath: '/crm/Customer', partyIdField: 'customerId', partyLabel: 'Customer', numberField: 'creditNoteNumber', dateField: 'issueDate', totalField: 'totalAmount' },
  DebitNote: { docTitle: 'DEBIT NOTE', lineBasePath: '/sales/DebitNoteLine', lineFk: 'debitNoteId', partyBasePath: '/purchase/Supplier', partyIdField: 'supplierId', partyLabel: 'Vendor', numberField: 'debitNoteNumber', dateField: 'issueDate', totalField: 'totalAmount' },
  // These doc types previously fell through to the generic key-value dump with
  // NO branding (no company name/logo/QR/signatory). Routed through the shared
  // branded layout so every one carries the full header + footer branding.
  DeliveryChallan: { docTitle: 'DELIVERY CHALLAN', lineBasePath: '/sales/DeliveryChallanLine', lineFk: 'deliveryChallanId', partyBasePath: '/crm/Customer', partyIdField: 'customerId', partyLabel: 'Ship To (Consignee)', numberField: 'challanNumber', dateField: 'challanDate', totalField: 'grandTotal', descFallback: true },
  Shipment: { docTitle: 'SHIPMENT / DISPATCH NOTE', lineBasePath: '/sales/ShipmentLine', lineFk: 'shipmentId', partyBasePath: '/crm/Customer', partyIdField: 'customerId', partyLabel: 'Ship To', numberField: 'shipmentNumber', dateField: 'shipmentDate', totalField: 'shippingCost' },
  PurchaseInvoice: { docTitle: 'PURCHASE INVOICE', lineBasePath: '/purchase/PurchaseInvoiceLine', lineFk: 'purchaseInvoiceId', partyBasePath: '/purchase/Supplier', partyIdField: 'supplierId', partyLabel: 'Vendor', numberField: 'invoiceNumber', dateField: 'invoiceDate', totalField: 'grandTotal' },
  PurchaseReturn: { docTitle: 'PURCHASE RETURN', lineBasePath: '/purchase/PurchaseReturnLine', lineFk: 'purchaseReturnId', partyBasePath: '/purchase/Supplier', partyIdField: 'supplierId', partyLabel: 'Vendor', numberField: 'returnNumber', dateField: 'returnDate', totalField: 'grandTotal' },
  PurchaseRequisition: { docTitle: 'PURCHASE REQUISITION', lineBasePath: '/purchase/PurchaseRequisitionLine', lineFk: 'purchaseRequisitionId', partyBasePath: '/purchase/Supplier', partyIdField: 'supplierId', partyLabel: 'Vendor', numberField: 'requisitionNumber', dateField: 'requisitionDate', totalField: 'grandTotal' },
};

export async function printRegistryDoc(resource: string, row: Record<string, unknown>): Promise<boolean> {
  const tenant = await getTenantHeaderInlined();
  const b = await getPrintBrandingInlined();

  // ----- Payment receipt (no line table) -----
  if (resource === 'SalesPayment' || resource === 'PurchasePayment') {
    const isPurchase = resource === 'PurchasePayment';
    const invBase = isPurchase ? '/purchase/PurchaseInvoice' : '/sales/SalesInvoice';
    const invFk = isPurchase ? 'purchaseInvoiceId' : 'salesInvoiceId';
    const inv = await resolveParty(invBase, row[invFk]);
    const partyBase = isPurchase ? '/purchase/Supplier' : '/crm/Customer';
    // Resolve the party from the payment row FIRST (it may carry customerId /
    // supplierId / customerName directly), then fall back to the linked
    // invoice — so the customer block is never blank on the print.
    const partyId = row.customerId ?? row.supplierId ?? inv.customerId ?? inv.supplierId;
    const party = partyId != null ? await resolveParty(partyBase, partyId) : {};
    const partyName = String(
      party.name ?? row.customerName ?? row.supplierName ?? inv.customerName ?? inv.supplierName ?? '',
    ) || '—';
    const partyAddress = String(party.address ?? party.billingAddress ?? '');
    const partyPhone = String(party.phone ?? party.mobile ?? '');
    const partyGstin = String(party.gstin ?? inv.customerGstin ?? '');
    const amount = Number(row.amount ?? 0) || 0;
    const number = String(row.code ?? row.paymentNumber ?? row.id ?? '');
    printPaymentReceipt({
      docTitle: isPurchase ? 'PURCHASE PAYMENT' : 'PAYMENT RECEIPT',
      number,
      date: String(row.paymentDate ?? row.indate ?? new Date().toISOString()),
      method: String(row.method ?? ''),
      txnRef: String(row.txnRef ?? ''),
      amount,
      tenant,
      partyLabel: isPurchase ? 'Paid To' : 'Received From',
      partyName,
      partyAddress,
      partyPhone,
      partyGstin,
      againstLabel: isPurchase ? 'Against Purchase Invoice' : 'Against Invoice',
      againstNumber: String(inv.invoiceNumber ?? inv.code ?? (row[invFk] != null ? `#${row[invFk]}` : '')),
      againstBalance: inv.balance != null ? Number(inv.balance) || 0 : undefined,
      status: String(row.paymentStatus ?? ''),
      signatureUrl: b.signatureUrl,
      qrUrl: printQrUrl(b.qrUrl, [isPurchase ? 'PURCHASE PAYMENT' : 'PAYMENT RECEIPT', number, partyName, 'Amt:' + amount, tenant.gstin]),
    });
    return true;
  }

  const cfg = DOC[resource];
  if (!cfg) return false;

  const allLines = await getList(cfg.lineBasePath);
  const mine = allLines
    .filter((l) => String(l[cfg.lineFk]) === String(row.id))
    .sort((a, b) => Number(a.lineNumber ?? 0) - Number(b.lineNumber ?? 0));
  let lines: SalesOrderPrintLine[] = mine.map((l) => {
    const qty = Number(l.quantity ?? l.qty ?? 0) || 0;
    // unitPrice (sales/purchase) → rate → estimatedUnitPrice (requisition).
    const rate = Number(l.unitPrice ?? l.rate ?? l.estimatedUnitPrice ?? 0) || 0;
    // lineTotal (challan/most) → estimatedTotal (requisition) → qty*rate.
    const lineTotal = Number(l.lineTotal ?? l.estimatedTotal ?? 0) || 0;
    return {
      description: String(l.productName ?? l.name ?? l.itemName ?? '—'),
      hsnCode: l.hsnCode ? String(l.hsnCode) : undefined,
      qty, unit: l.unitOfMeasure ? String(l.unitOfMeasure) : undefined, rate,
      amount: r2(rate ? qty * rate : lineTotal),
    };
  });
  // Some docs (DeliveryChallan) keep their items in the header `description`
  // JSON instead of a line table — recover them so the print isn't item-less.
  if (lines.length === 0 && cfg.descFallback) {
    try {
      const env = JSON.parse(String(row.description ?? '{}')) as { lines?: Array<Record<string, unknown>>; items?: Array<Record<string, unknown>> };
      const src = (Array.isArray(env.lines) && env.lines.length ? env.lines : (Array.isArray(env.items) ? env.items : []));
      lines = src.map((l) => {
        const qty = Number(l.quantity ?? l.qty ?? 0) || 0;
        const rate = Number(l.unitPrice ?? l.rate ?? l.price ?? 0) || 0;
        return {
          description: String(l.name ?? l.productName ?? l.itemName ?? '—'),
          qty, unit: l.unit ? String(l.unit) : (l.unitOfMeasure ? String(l.unitOfMeasure) : undefined), rate,
          amount: r2(qty * rate),
        };
      });
    } catch { /* no envelope */ }
  }

  const party = await resolveParty(cfg.partyBasePath, row[cfg.partyIdField]);
  const billTo: PartyBlock = {
    label: cfg.partyLabel,
    name: String(party.name ?? row.customerName ?? row.supplierName ?? ''),
    address: String(party.address ?? party.billingAddress ?? row.billingAddress ?? ''),
    city: party.city ? String(party.city) : undefined,
    state: party.state ? String(party.state) : (row.placeOfSupply ? String(row.placeOfSupply) : undefined),
    pincode: String(party.pincode ?? party.pinCode ?? ''),
    gstin: String(party.gstin ?? row.customerGstin ?? ''),
    phone: String(party.phone ?? party.mobile ?? ''),
    email: String(party.email ?? ''),
  };

  const subTotal = r2(lines.reduce((s, l) => s + l.amount, 0) || Number(row.subTotal ?? 0) || 0);
  const taxAmount = r2(Number(row.taxAmount ?? 0) || 0);
  const discount = r2(Number(row.discountAmount ?? 0) || 0);
  const transport = r2(Number(row.shippingCharge ?? 0) || 0);
  const totalAmount = r2(Number(row[cfg.totalField] ?? row.grandTotal ?? row.totalAmount ?? (subTotal - discount + taxAmount + transport)) || 0);
  const roundOff = r2(Number(row.roundingAmount ?? 0) || (totalAmount - (subTotal - discount + taxAmount + transport)));
  // CGST/SGST vs IGST from GSTIN state codes (first 2 digits).
  const custState = String(billTo.gstin || '').slice(0, 2);
  const tenantState = String(tenant.gstin || '').slice(0, 2);
  const interstate = !!custState && !!tenantState && custState !== tenantState;

  const docNumber = String(row[cfg.numberField] ?? row.code ?? row.id ?? '');
  const input: SalesOrderPrintInput = {
    docTitle: cfg.docTitle,
    number: docNumber,
    date: String(row[cfg.dateField] ?? row.indate ?? new Date().toISOString()),
    buyerPoNumber: row.buyerPoNumber ? String(row.buyerPoNumber) : undefined,
    tenant, billTo,
    lines,
    subTotal, tradeDiscount: discount, transportCharges: transport,
    taxableAmount: r2(Math.max(0, subTotal - discount)),
    cgstAmount: interstate ? 0 : r2(taxAmount / 2),
    sgstAmount: interstate ? 0 : r2(taxAmount / 2),
    igstAmount: interstate ? taxAmount : 0,
    roundOff, totalAmount,
    transporter: row.transporterName ? String(row.transporterName) : undefined,
    bank: (b.bankName || b.bankAccountNo) ? {
      accountName: b.bankAccountName, bankName: b.bankName, accountNo: b.bankAccountNo, ifsc: b.bankIfsc, branch: b.bankBranch,
    } : undefined,
    signatureUrl: b.signatureUrl,
    qrUrl: printQrUrl(b.qrUrl, [cfg.docTitle, docNumber, billTo.name, 'Amt:' + totalAmount, tenant.gstin]),
  };
  printSalesOrder(input);
  return true;
}
